import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wrench, ShieldAlert, KeyRound, Cpu, ScrollText, MapPin, Search,
  CheckCircle2, ArrowRight, Loader2, Lock, Sparkles, Database,
  AlertTriangle, Globe,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Location, CitationOrder } from "@shared/schema";

const PATENT_CARDS = [
  {
    id: "us12536233b1",
    code: "US12536233B1",
    name: "AI-Generated Content Pages",
    summary:
      "Google literally builds AI landing pages from your GBP data when your site underperforms. More services = more raw material Google's AI uses to represent you.",
    icon: Sparkles,
  },
  {
    id: "us10402435",
    code: "US10402435",
    name: "Knowledge Panel Management",
    summary:
      "Knowledge Panels pull directly from GBP. Every service entry feeds the panel and shows up in the entity card next to your name.",
    icon: Database,
  },
  {
    id: "us9135625",
    code: "US9135625",
    name: "Fraud / Quality Classifier",
    summary:
      "Why manual stacking is safe — the spam classifier targets keyword-stuffed business names, not service lists. Services are an untouched surface.",
    icon: ShieldAlert,
  },
];

const NEAR_ME_REQUIREMENTS = [
  { label: "Service match", body: "Manual entries hit it." },
  { label: "Location match", body: "Manual entries hit it." },
  { label: "Entity relevance", body: "Manual entries hit it." },
];

const DELIVERABLES = [
  "100–2,000 manually stacked services per order ($49 / 100 services)",
  "Location-modifier variants (\"near me\", neighborhood, ZIP, suburb)",
  "Service descriptions tuned for AI Overview citation",
  "Monthly refresh as Google rotates relevance signals",
  "Audit doc showing exactly what was added, where, and why",
  "No automation — every entry by a human, no API risk",
];

const FAQ_ITEMS = [
  {
    q: "Is this against Google's TOS?",
    a: "No. Manual entry through the GBP dashboard is the supported path. The 100-cap is an API limitation, not a policy limit.",
  },
  {
    q: "Will this trigger spam filters?",
    a: "Per US9135625, Google's quality classifier targets keyword-stuffed business names, not service lists. Services are a separate surface that the classifier does not police the same way.",
  },
  {
    q: "How long until I see lift?",
    a: "14–30 days for Map Pack movement. 30–60 days for AI Overview citations as Google's models retrain on your expanded entity footprint.",
  },
  {
    q: "What if I already have 100 services?",
    a: "Perfect. We start where the API stopped and add the next 100–400 manually, then handle monthly refresh.",
  },
];

export default function ServicesStackPage() {
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations[0]?.id || "";

  const { data: orders = [] } = useQuery<CitationOrder[]>({
    queryKey: ["/api/citation-orders", locationId],
    enabled: !!locationId,
  });

  const stackOrder = orders.find(
    (o) => o.packageType === "services_single" && o.status === "paid",
  );

  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    apiRequest("GET", `/api/citation-orders/verify-session?session_id=${sessionId}`)
      .then((r) => r.json())
      .then((order) => {
        if (order?.status === "paid") {
          toast({
            title: "Payment confirmed",
            description: "Your service stacking job is queued. We'll start within one business day.",
          });
          queryClient.invalidateQueries({ queryKey: ["/api/citation-orders", locationId] });
        }
        window.history.replaceState({}, "", "/services");
      })
      .catch(() => {
        window.history.replaceState({}, "", "/services");
      });
  }, [sessionId, locationId, toast]);

  // Pack quantity: 1 pack = 100 services @ $49. Range: 1..20 (100..2000 services).
  const [packCount, setPackCount] = useState<number>(1);
  const serviceCount = packCount * 100;
  const totalPrice = packCount * 49;

  const checkout = useMutation({
    mutationFn: async (overridePackCount?: number) => {
      if (!locationId) throw new Error("Pick a location first");
      const requestedPacks = typeof overridePackCount === "number" ? overridePackCount : packCount;
      const res = await apiRequest("POST", "/api/citation-orders/checkout", {
        locationId,
        packageType: "services_single",
        packCount: requestedPacks,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({
        title: "Checkout failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Each order is independent now ($49 / 100 services per order). We surface
  // whether this location has at least one paid stack so we can show a soft
  // "previously stacked" hint, but we no longer disable the buy button — users
  // can come back and purchase additional service packs.
  const isInstalled = !!stackOrder;
  const buyDisabled = !locationId || checkout.isPending;

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="container max-w-6xl py-8 px-6 md:px-10 space-y-12 overflow-y-auto h-full pb-24" data-testid="container-services-stack">
      {/* HERO */}
      <section className="space-y-6" data-testid="section-hero">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-orange-500" />
          </div>
          <Badge variant="outline" className="border-orange-500/40 text-orange-500">
            Service Stacking
          </Badge>
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight max-w-4xl" data-testid="text-hero-headline">
          Google's API caps you at 100 services.{" "}
          <span className="text-orange-500">We stack hundreds more — by hand.</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl" data-testid="text-hero-sub">
          The back door into AI Overviews, AI Map Pack, and "near me" rankings that the API will never give you.
        </p>

        <div className="flex flex-col md:flex-row gap-4 md:items-stretch">
          {/* Hero buy panel — keeps the actual checkout above the fold */}
          <div
            className="flex-1 rounded-xl border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/10 via-background to-red-500/10 p-5 space-y-3 shadow-lg shadow-orange-500/10"
            data-testid="panel-hero-buy"
          >
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="text-2xl font-bold">
                <span className="text-orange-500" data-testid="text-hero-total-price">${totalPrice}</span>
                <span className="text-base font-normal text-muted-foreground ml-2">
                  for {serviceCount.toLocaleString()} services
                </span>
              </div>
              <Badge variant="outline" className="border-orange-500/40 text-orange-500 text-[10px]">
                $49 / 100 services
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Select value={locationId} onValueChange={setSelectedLocationId}>
                <SelectTrigger className="w-full" data-testid="select-location-hero">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`option-location-hero-${l.id}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={String(packCount)}
                onValueChange={(v) => setPackCount(Number(v))}
              >
                <SelectTrigger className="w-full" data-testid="select-pack-count-hero">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                    <SelectItem
                      key={n}
                      value={String(n)}
                      data-testid={`option-pack-count-${n}`}
                    >
                      {(n * 100).toLocaleString()} services — ${n * 49}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              size="lg"
              onClick={() => checkout.mutate(undefined)}
              disabled={buyDisabled}
              className="w-full bg-gradient-to-r from-brand-orange to-orange-600 hover:from-brand-orange/90 hover:to-orange-700 text-white shadow-lg shadow-brand-orange/20"
              data-testid="button-hero-cta"
            >
              {checkout.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="w-4 h-4 mr-2" />
              )}
              Stack {serviceCount.toLocaleString()} services — ${totalPrice}
            </Button>
            <div className="flex items-center justify-between text-xs text-muted-foreground gap-3">
              <span>
                {!locationId
                  ? "Pick a location to enable checkout."
                  : isInstalled
                    ? "Already stacked once — buy more service packs anytime."
                    : "Hand-stacked by our team. No API."}
              </span>
            </div>
          </div>

        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="space-y-3" data-testid="section-problem">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h2 className="text-2xl font-bold">The problem most agencies refuse to solve</h2>
        </div>
        <p className="text-base text-muted-foreground max-w-3xl">
          The Google Business Profile API only allows 100 services per listing. Most agencies hit that ceiling and stop —
          they tell you "Google won't let us add more." Meanwhile, the agency two blocks over manually adds 300, 400,
          500 services and quietly owns every "[service] near me" query in your service area.
        </p>
      </section>

      {/* THE LOOPHOLE */}
      <Card className="border-2 border-orange-500/30 bg-gradient-to-br from-background via-background to-orange-500/5" data-testid="card-loophole">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" data-testid="text-loophole-title">The loophole</h2>
              <p className="text-sm text-muted-foreground">Manual entry has no cap.</p>
            </div>
          </div>
          <p className="text-base leading-relaxed max-w-3xl">
            Every service you add by hand is a separate entity Google can match to a query.{" "}
            <strong className="text-foreground">More services = more relevance signals = more AI Overview citations and more Map Pack appearances.</strong>{" "}
            We do the manual stacking the API won't let your agency touch.
          </p>
        </CardContent>
      </Card>

      {/* PATENT EVIDENCE */}
      <section className="space-y-4" data-testid="section-patents">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-orange-500" />
          <h2 className="text-2xl font-bold">The patent evidence</h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          These aren't theories. They're filed, granted Google patents that describe exactly how more service entries
          turn into more rankings.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {PATENT_CARDS.map((p) => (
            <Card key={p.id} className="border-orange-500/20 hover-elevate" data-testid={`card-patent-${p.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-orange-500" />
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono shrink-0" data-testid={`badge-patent-${p.id}`}>
                    {p.code}
                  </Badge>
                </div>
                <h3 className="font-semibold text-sm leading-tight" data-testid={`text-patent-name-${p.id}`}>
                  {p.name}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.summary}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* AI-SUGGESTED SERVICES */}
      <Card className="border-orange-500/20" data-testid="card-ai-suggested">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-500" />
            <h3 className="text-xl font-bold">Google's AI-suggested services on your GBP</h3>
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Google now surfaces AI service snippets <strong className="text-foreground">above your address and phone</strong> on
            mobile listings. The more services you've defined, the more snippet inventory Google has to draw from when
            it decides what to show searchers. Empty service list = empty snippet rotation.
          </p>
        </CardContent>
      </Card>

      {/* VERTEX AI */}
      <Card className="border-orange-500/20" data-testid="card-vertex">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-orange-500" />
            <h3 className="text-xl font-bold">Vertex AI + Gemini + BigQuery feed on structured entity data</h3>
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Google's stack analyzes local entity data to generate personalized AI content for every search session. Your
            service list <em>is</em> structured entity data. Stop starving the machine —{" "}
            <strong className="text-foreground">feed it everything you actually do.</strong>
          </p>
        </CardContent>
      </Card>

      {/* NEAR ME */}
      <section className="space-y-4" data-testid="section-near-me">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-orange-500" />
          <h2 className="text-2xl font-bold">What "near me" actually targets</h2>
        </div>
        <Card className="border-orange-500/30 bg-gradient-to-br from-background to-orange-500/5">
          <CardContent className="p-6 space-y-5">
            <div className="text-center space-y-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-sm font-mono">
                <Search className="w-4 h-4 text-orange-500" /> "Plumber Near Me"
              </div>
              <div className="text-xs text-muted-foreground">Google needs three things to rank you here:</div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              {NEAR_ME_REQUIREMENTS.map((r, i) => (
                <div
                  key={r.label}
                  className="p-4 rounded-lg border-2 border-orange-500/30 bg-background flex flex-col items-center text-center gap-2"
                  data-testid={`tile-near-me-${i}`}
                >
                  <Badge className="bg-orange-500/15 text-orange-500 border-orange-500/30">{r.label}</Badge>
                  <div className="flex items-center gap-1 text-sm font-semibold text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {r.body}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mb-1">Manual stacking</div>
                <div className="text-xs text-muted-foreground">Hits all three signals. You rank.</div>
              </div>
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="text-sm font-semibold text-red-600 dark:text-red-400 mb-1">API-only listing</div>
                <div className="text-xs text-muted-foreground">Hits one signal. You get buried.</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* WHAT YOU GET */}
      <section className="space-y-4" data-testid="section-deliverables">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-orange-500" />
          <h2 className="text-2xl font-bold">What you get</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {DELIVERABLES.map((d) => (
            <div
              key={d}
              className="flex items-start gap-3 p-4 rounded-lg border bg-card"
              data-testid={`item-deliverable-${d.slice(0, 20).toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
              <span className="text-sm">{d}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="space-y-4 scroll-mt-8" data-testid="section-pricing">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-orange-500" />
          <h2 className="text-2xl font-bold">Lock in your stack</h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          {/* Single Location — checkout card */}
          <Card
            className="lg:col-span-2 border-2 border-orange-500/30 hover-elevate flex flex-col"
            data-testid="card-tier-single"
          >
            <CardHeader>
              <CardTitle className="text-lg">Single Location</CardTitle>
              <CardDescription>One GBP, packs of 100 services.</CardDescription>
              <div className="text-3xl font-bold mt-2" data-testid="text-pricing-total">
                ${totalPrice}
                <span className="text-sm font-normal text-muted-foreground">
                  {" "}for {serviceCount.toLocaleString()} services
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">$49 per 100 services · one-time</div>
            </CardHeader>
            <CardContent className="flex-1 space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> Choose 100–2,000 services per order</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> Location-modifier variants</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> AI Overview-tuned descriptions</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> Audit doc</li>
              </ul>

              <Select value={locationId} onValueChange={setSelectedLocationId}>
                <SelectTrigger data-testid="select-location-services">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`option-location-${l.id}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={String(packCount)}
                onValueChange={(v) => setPackCount(Number(v))}
              >
                <SelectTrigger data-testid="select-pack-count-pricing">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                    <SelectItem
                      key={n}
                      value={String(n)}
                      data-testid={`option-pack-count-pricing-${n}`}
                    >
                      {(n * 100).toLocaleString()} services — ${n * 49}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={() => checkout.mutate(undefined)}
                disabled={buyDisabled}
                size="lg"
                className="w-full bg-gradient-to-r from-brand-orange to-orange-600 hover:from-brand-orange/90 hover:to-orange-700 text-white shadow-lg shadow-brand-orange/20"
                data-testid="button-buy-services-single"
              >
                {checkout.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wrench className="w-4 h-4 mr-2" />
                )}
                Stack {serviceCount.toLocaleString()} services — ${totalPrice}
              </Button>

              {!locationId && (
                <p className="text-xs text-muted-foreground" data-testid="hint-select-location-services">
                  Pick a location to enable checkout.
                </p>
              )}
            </CardContent>
          </Card>

          {/* What you actually get — geo expansion explainer */}
          <Card
            className="lg:col-span-3 border-2 border-orange-500/20 bg-gradient-to-br from-background via-background to-orange-500/5 flex flex-col"
            data-testid="card-geo-expansion"
          >
            <CardHeader>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-500" />
                <CardTitle className="text-lg">How we expand your map reach</CardTitle>
              </div>
              <CardDescription>
                Every service we stack is rewritten around the geography Google's AI Overview already trusts —
                neighborhoods, landmarks, and the nearby cities your competitors forgot to claim.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div
                  className="rounded-lg border border-orange-500/20 bg-background/40 p-4 space-y-2"
                  data-testid="card-geo-neighborhoods"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    <h4 className="font-semibold text-sm">Neighborhoods</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    We mine every micro-area Google associates with your category — districts, suburbs, ZIPs, HOAs —
                    and write a service variant for each. "Towing in Summerlin," "Towing in Spring Valley,"
                    "Towing near Henderson" all become first-class entries on your profile.
                  </p>
                </div>
                <div
                  className="rounded-lg border border-orange-500/20 bg-background/40 p-4 space-y-2"
                  data-testid="card-geo-landmarks"
                >
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-orange-500" />
                    <h4 className="font-semibold text-sm">Landmarks</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Stadiums, airports, hospitals, freeway exits, tourist corridors — the proper nouns AI Overview
                    pulls into "near me" answers. We bake them into descriptions so your GBP shows up the moment a
                    user mentions one in a query.
                  </p>
                </div>
                <div
                  className="rounded-lg border border-orange-500/20 bg-background/40 p-4 space-y-2"
                  data-testid="card-geo-nearby-cities"
                >
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-orange-500" />
                    <h4 className="font-semibold text-sm">Nearby cities</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    The towns 10–25 miles out where you can legitimately serve but the map never ranks you. We add
                    geo-modifier services for every reachable city so your pin starts surfacing well outside your
                    home grid.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-orange-500" />
                  <h4 className="font-semibold text-sm">Descriptions tuned to win the AI Overview</h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Each service description is hand-written to match the exact phrasing Gemini and SGE quote in their
                  generative answers — entity-rich, intent-aligned, and packed with the location signals that earn
                  the citation. We're not stuffing keywords; we're feeding the model the structured language it
                  already cites.
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 mt-0.5 shrink-0" />
                    Entity-anchored copy (city + service + modifier)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 mt-0.5 shrink-0" />
                    Natural-language Q&amp;A patterns AI Overview rewards
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 mt-0.5 shrink-0" />
                    "Near me" + neighborhood + landmark co-occurrence
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 mt-0.5 shrink-0" />
                    Schema-friendly phrasing the Knowledge Graph picks up
                  </li>
                </ul>
              </div>

              <p className="text-xs text-muted-foreground italic">
                Net effect: the same GBP, the same address — but a footprint that stretches across every
                neighborhood, landmark, and nearby city Google's AI is willing to associate with you.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="space-y-4" data-testid="section-faq">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-orange-500" />
          <h2 className="text-2xl font-bold">FAQ</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {FAQ_ITEMS.map((f, i) => (
            <Card key={i} className="hover-elevate" data-testid={`card-faq-${i}`}>
              <CardContent className="p-5 space-y-2">
                <h3 className="font-semibold text-sm flex items-start gap-2">
                  <span className="text-orange-500">Q.</span> {f.q}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed pl-5">
                  {f.a}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
